import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { featureFlags } from '../../config/featureFlags';
import { useAuth } from '../../contexts/AuthContext';
import { AuthPromptTab, useAuthPrompt } from '../../contexts/AuthPromptContext';
import { useChat } from '../../contexts/ChatContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { FILLS, FONTS, GLASS, RADII, STROKES } from '../../styles/tokens';
import { generateAriaLabel, getSkipLinkProps } from '../../utils/aria';
import { isPlainGuestLinkClick } from '../../utils/postLoginRedirect';
import CartDropdown from '../cart/CartDropdown';
import Avatar from '../common/Avatar';
import { MenuItem } from './MenuItems';
import MobileAppBar from './MobileAppBar';
import MobileMenuSheet from './MobileMenuSheet';
import { getCoreNavigationGroups, getHomePath, getUserMenuItems } from './navigationHelpers';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();
  const { isChatOpen, toggleChat } = useChat();
  const { openAuthPrompt } = useAuthPrompt();
  const userFirstName = user ? user.full_name.split(' ')[0] : 'User';

  // A plain left click opens the sign-in pop-up; modified clicks and auth pages keep the link.
  const handleGuestLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    initialTab: AuthPromptTab,
  ) => {
    if (!isPlainGuestLinkClick(event, location.pathname)) return;
    event.preventDefault();
    openAuthPrompt({ intent: 'signIn', initialTab });
  };

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isMobile = useIsMobile();

  const isAdminPage = location.pathname.startsWith('/admin');
  const showChat = featureFlags.chatbot && !isAdminPage && !isMobile;
  const navRightOffset = showChat && isChatOpen ? '380px' : '0px';

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
    setUserMenuOpen(false);
  };

  const handleLogoutConfirm = () => {
    logout();
    navigate('/');
    setLogoutDialogOpen(false);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleUserMenuToggle = () => {
    setUserMenuOpen((prev) => !prev);
  };

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(e.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  // Native dialog management
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (logoutDialogOpen && !dialog.open) {
      dialog.showModal();
    } else if (!logoutDialogOpen && dialog.open) {
      dialog.close();
    }
  }, [logoutDialogOpen]);

  const Logo = useCallback(
    () => (
      <Link to={getHomePath(user)} style={{ display: 'block', textDecoration: 'none' }}>
        <img
          src="/assets/belhoplogo.png"
          alt="Belhop"
          style={{
            height: '36px',
            display: 'block',
            cursor: 'pointer',
          }}
        />
      </Link>
    ),
    [user],
  );

  // Shared per-item render for both nav groups below, so the two groups stay
  // pixel-identical instead of duplicating the Link markup twice.
  const renderNavItem = (item: MenuItem) => {
    const pathMatches =
      location.pathname === item.path || (item.path === '/' && location.pathname === '/discover');
    const stateMatches =
      !item.requiresStateMatch ||
      (item.state &&
        location.state &&
        JSON.stringify(location.state) === JSON.stringify(item.state));
    const isActive = pathMatches && stateMatches;

    return (
      <Link
        key={item.text}
        to={item.path}
        state={item.state}
        aria-label={generateAriaLabel.navigation.goTo(item.text)}
        style={{
          padding: '8px 16px',
          borderRadius: RADII.pill,
          fontSize: '12px',
          fontWeight: 500,
          letterSpacing: '0.03em',
          color: isActive ? FILLS.gold : FILLS.textMuted,
          backgroundColor: isActive ? FILLS.goldAlpha05 : 'transparent',
          border: isActive ? `1px solid ${FILLS.goldAlpha30}` : '1px solid transparent',
          cursor: 'pointer',
          transition: 'all 0.25s',
          textTransform: 'uppercase',
          fontFamily: FONTS.body,
          textDecoration: 'none',
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.color = FILLS.textPrimary;
            e.currentTarget.style.backgroundColor = FILLS.chip;
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.color = FILLS.textMuted;
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        {item.renderLabel ? item.renderLabel() : item.text}
      </Link>
    );
  };

  const coreNavigationGroups = getCoreNavigationGroups(user ?? undefined);

  return (
    <>
      {/* Skip Navigation Links */}
      <div style={{ position: 'absolute', top: '-100px', left: 0 }}>
        <a {...getSkipLinkProps('main-content')}>Skip to main content</a>
        <a {...getSkipLinkProps('main-navigation')}>Skip to navigation</a>
      </div>

      {isMobile ? (
        <>
          <MobileAppBar onMenuClick={() => setMenuOpen(true)} />
          <MobileMenuSheet
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            onLogoutClick={handleLogoutClick}
          />
        </>
      ) : (
        <header
          role="banner"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: navRightOffset,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            // Centre the header content on the same 1280 axis as the hero and
            // the catalogue below: the side padding grows past 48px once the
            // viewport exceeds 1280 so the logo and the right cluster line up
            // with the page column instead of hugging the screen edge.
            padding: '0 max(48px, calc(50vw - 592px))',
            height: '72px',
            background: GLASS.nav.background,
            backdropFilter: GLASS.nav.blur,
            WebkitBackdropFilter: GLASS.nav.blur,
            borderBottom: GLASS.nav.border,
            transition: 'right 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Left: Logo + Nav Links */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Logo />
            <nav
              role="navigation"
              aria-label="Main navigation"
              id="main-navigation"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginLeft: '40px',
              }}
            >
              {coreNavigationGroups.primary.map(renderNavItem)}
              <span
                aria-hidden="true"
                style={{
                  width: '1px',
                  height: '18px',
                  background: STROKES.subtle,
                  // Widened from 4px each side so the two nav groups read as
                  // clearly split rather than just loosely spaced (owner
                  // feedback at the visual checkpoint).
                  margin: '0 18px',
                  flexShrink: 0,
                }}
              />
              {coreNavigationGroups.secondary.map(renderNavItem)}
            </nav>
          </div>

          {/* Right: Concierge toggle (authenticated only) + Cart (everyone) + Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isAuthenticated && showChat && (
              <button
                onClick={toggleChat}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 18px',
                  borderRadius: RADII.pill,
                  cursor: 'pointer',
                  border: isChatOpen ? `1px solid ${FILLS.gold}` : `1px solid ${FILLS.goldAlpha30}`,
                  background: isChatOpen ? FILLS.gold : FILLS.goldAlpha05,
                  color: isChatOpen ? FILLS.page : FILLS.gold,
                  fontFamily: FONTS.body,
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  transition: 'all 0.3s',
                }}
                onMouseEnter={(e) => {
                  if (!isChatOpen) {
                    e.currentTarget.style.background = FILLS.goldAlpha10;
                    e.currentTarget.style.borderColor = FILLS.gold;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isChatOpen) {
                    e.currentTarget.style.background = FILLS.goldAlpha05;
                    e.currentTarget.style.borderColor = FILLS.goldAlpha30;
                  }
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
                <span>Concierge</span>
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: isChatOpen ? FILLS.page : FILLS.success,
                    animation: 'dotPulse 2.5s ease-in-out infinite',
                  }}
                />
              </button>
            )}

            <CartDropdown />

            {isAuthenticated ? (
              <div style={{ position: 'relative' }}>
                <button
                  ref={menuButtonRef}
                  onClick={handleUserMenuToggle}
                  title={user?.full_name || 'User Menu'}
                  aria-label={user?.full_name || 'User menu'}
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                  style={{
                    borderRadius: '50%',
                    transition: 'box-shadow 0.2s',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${FILLS.goldAlpha30}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Avatar
                    key={user?.profile_picture_url}
                    url={user?.profile_picture_url}
                    name={user?.full_name ?? ''}
                    size={36}
                  />
                </button>

                {/* Dropdown Menu */}
                {userMenuOpen && (
                  <div
                    ref={menuRef}
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      width: '200px',
                      background: FILLS.surface,
                      border: `1px solid ${STROKES.subtle}`,
                      borderRadius: RADII.card,
                      overflow: 'hidden',
                      zIndex: 200,
                    }}
                  >
                    {getUserMenuItems(userFirstName, user).map((item) => (
                      <Link
                        key={item.text}
                        to={item.path}
                        onClick={() => setUserMenuOpen(false)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '12px 18px',
                          fontSize: '13px',
                          color: FILLS.textLight,
                          background: 'none',
                          border: 'none',
                          borderBottom: `1px solid ${STROKES.subtle}`,
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: FONTS.body,
                          transition: 'all 0.15s',
                          textDecoration: 'none',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = FILLS.surfaceActive;
                          e.currentTarget.style.color = FILLS.gold;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = FILLS.textLight;
                        }}
                      >
                        {item.text}
                      </Link>
                    ))}
                    <div style={{ height: '1px', background: STROKES.subtle }} />
                    <button
                      onClick={handleLogoutClick}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '12px 18px',
                        fontSize: '13px',
                        color: FILLS.textLight,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: FONTS.body,
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = FILLS.surfaceActive;
                        e.currentTarget.style.color = FILLS.gold;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = FILLS.textLight;
                      }}
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Guest nav — outline "Sign In" + solid "Sign Up", per v2 mockup .nav-guest-btn
              <>
                <Link
                  to="/login"
                  onClick={(event) => handleGuestLinkClick(event, 'logIn')}
                  style={{
                    padding: '8px 18px',
                    borderRadius: RADII.pill,
                    fontFamily: FONTS.body,
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: 'transparent',
                    border: `1px solid ${STROKES.subtle}`,
                    color: FILLS.textLight,
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = FILLS.goldAlpha30;
                    e.currentTarget.style.color = FILLS.gold;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = STROKES.subtle;
                    e.currentTarget.style.color = FILLS.textLight;
                  }}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={(event) => handleGuestLinkClick(event, 'signUp')}
                  style={{
                    padding: '8px 18px',
                    borderRadius: RADII.pill,
                    fontFamily: FONTS.body,
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: FILLS.gold,
                    border: `1px solid ${FILLS.gold}`,
                    color: FILLS.page,
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = FILLS.goldWarm;
                    e.currentTarget.style.borderColor = FILLS.goldWarm;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = FILLS.gold;
                    e.currentTarget.style.borderColor = FILLS.gold;
                  }}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </header>
      )}

      {/* Logout Confirmation Dialog — matches mockup .modal#logoutModal */}
      <dialog
        ref={dialogRef}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        onClose={handleLogoutCancel}
        style={{
          background: FILLS.surface,
          border: `1px solid ${STROKES.subtle}`,
          borderRadius: RADII.card,
          padding: '32px',
          color: FILLS.textPrimary,
          maxWidth: '460px',
          width: '100%',
          boxShadow: '0 32px 80px rgba(0, 0, 0, 0.7)',
        }}
      >
        {/* Circular gold-tinted icon — mockup .modal-icon (↪ glyph) */}
        <div
          aria-hidden="true"
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: FILLS.goldAlpha05,
            border: `1px solid ${FILLS.goldAlpha30}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: FILLS.gold,
            margin: '0 auto 16px',
            fontSize: '22px',
            lineHeight: 1,
            fontFamily: FONTS.body,
          }}
        >
          ↪
        </div>
        <h3
          id="logout-dialog-title"
          style={{
            fontFamily: FONTS.display,
            fontSize: '24px',
            fontWeight: 500,
            textAlign: 'center',
            color: FILLS.textPrimary,
            margin: '0 0 8px',
            lineHeight: 1.2,
          }}
        >
          Sign out of Belhop?
        </h3>
        <p
          id="logout-dialog-description"
          style={{
            fontSize: '14px',
            color: FILLS.textMuted,
            textAlign: 'center',
            lineHeight: 1.6,
            margin: '0 0 24px',
            fontFamily: FONTS.body,
          }}
        >
          You&apos;ll need to sign in again to access your trips, wishlist, and concierge chat.
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleLogoutCancel}
            aria-label="Stay signed in"
            style={{
              flex: 1,
              padding: '12px 18px',
              background: 'transparent',
              border: `1px solid ${STROKES.subtle}`,
              borderRadius: RADII.control,
              color: FILLS.textPrimary,
              fontFamily: FONTS.body,
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'border-color 0.15s ease, color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = FILLS.goldAlpha30;
              e.currentTarget.style.color = FILLS.gold;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = STROKES.subtle;
              e.currentTarget.style.color = FILLS.textPrimary;
            }}
          >
            Stay signed in
          </button>
          <button
            onClick={handleLogoutConfirm}
            aria-label="Sign out"
            style={{
              flex: 1,
              padding: '12px 18px',
              background: FILLS.gold,
              border: 'none',
              borderRadius: RADII.control,
              color: FILLS.page,
              fontFamily: FONTS.body,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = FILLS.goldWarm;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = FILLS.gold;
            }}
          >
            Sign out
          </button>
        </div>
      </dialog>
    </>
  );
};

export default Header;
